import { useEffect, useRef, useState } from 'react'
import { useCustomer, useValidate } from '../api/queries'
import { api, errorMessage } from '../api/client'
import { buildPayload } from '../api/loyalty'
import { useFlow } from '../flow/FlowContext'
import { useSession } from '../session/SessionContext'
import { getLightspeedApi } from '../lightspeed/pos-api'
import { LoadingScreen } from './LoadingScreen'
import { t } from '../i18n'

/** The `pointsGiven` step: fetch the customer, link the sale, and record the visit points, then
 * go straight to the profile.
 *
 * There used to be an account-linked celebration here (person + check badge + sparkles) that ran
 * for a fixed 1.5s plus a 320ms swipe before the profile appeared. It is gone: at a till that is
 * ~2s of dead time on every single scan, and the merchants' benchmark for this integration is
 * "faster than pulling out a phone" (PRD 1/§1). The scan is already acknowledged instantly by the
 * sound cue, and the profile appearing IS the confirmation — the animation only delayed it.
 *
 * A plain loader now covers the fetch, so the wait is exactly as long as the network takes and
 * no longer.
 *
 * Amount = the per-visit `stamp_amount` from /validate (the POS already determines it), default 1.
 * Any fetch/award failure surfaces on the error screen. */

export function ScanFlow() {
  const { dispatch } = useFlow()
  const { scannedQr } = useSession()
  const customer = useCustomer(scannedQr)
  const validate = useValidate()
  const started = useRef(false)
  const [awarded, setAwarded] = useState(false)

  useEffect(() => {
    if (customer.isError) {
      dispatch({ type: 'CUSTOMER_FAILED', message: errorMessage(customer.error) })
    }
  }, [customer.isError, customer.error, dispatch])

  // Link the open sale to this Badger consumer so the purchase is attributed to them, and tag
  // the sale with the consumer's id as a BADGER external reference. The external reference is
  // what echoes back on the order-closed webhook (the consumer record does not), so the visit
  // stamp can be committed at confirmed payment even when no reward is redeemed.
  useEffect(() => {
    if (!customer.data) return
    const ls = getLightspeedApi()
    ls.setConsumer({
      identifier: customer.data.consumer.consumer_id,
      firstName: customer.data.consumer.display_name,
    })
    ls.addExternalReference(customer.data.consumer.consumer_id)
  }, [customer.data])

  // Auto-award the scan points once.
  useEffect(() => {
    if (!customer.isSuccess || started.current) return
    started.current = true
    const data = customer.data
    const amt = validate.data?.config?.stamp_amount ?? 1
    void (async () => {
      try {
        const account = await getLightspeedApi().getCurrentAccount()
        await api.pending(buildPayload(account, data, { stamp_amount: amt, is_redemption: false }))
        // M2 buffers the stamp and only commits it at confirmed payment, so we do NOT optimistically
        // bump the displayed count — the profile shows the real current stamps (see CustomerInfoScreen).
        setAwarded(true)
      } catch (e) {
        dispatch({ type: 'SUBMIT_FAILED', message: errorMessage(e) })
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.isSuccess])

  // Straight to the profile as soon as the points are recorded — no artificial hold.
  useEffect(() => {
    if (awarded) dispatch({ type: 'SHOW_INFO' })
  }, [awarded, dispatch])

  return <LoadingScreen label={t('loadingCustomer')} />
}
